// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/TotalsCard.jsx
// *******************************************************************************
// INVOICE TOTALS CARD
// *******************************************************************************
// Displays invoice totals: products, discount, freight, insurance, taxes, grand total.
// Uses backend-calculated totals when available, falls back to item-level calculation.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Row, Col, Typography, Descriptions, Divider, theme } from 'antd';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import fiscalService from 'services/fiscalService';

const { Title, Text } = Typography;
const { useToken } = theme;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TotalsCard = ({ items = [], data }) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { token } = useToken();

  // 3. LOCAL VARIABLES I

  // 7. LOCAL VARIABLES II
  const totals = useMemo(() => {
    if (data?.totals) {
      const discountAmount = data.totals.discountAmount || 0;
      const grossProductsTotal = (data.totals.productsAmount || 0) + discountAmount;

      return {
        productsTotal: grossProductsTotal,
        discount: discountAmount,
        freight: data.totals.freightAmount || 0,
        insurance: data.totals.insuranceAmount || 0,
        otherExpenses: data.totals.otherExpensesAmount || 0,
        invoiceTotal: data.totals.documentAmount || 0,
        icmsValue: data.totals.icmsAmount || 0,
        pisValue: data.totals.pisAmount || 0,
        cofinsValue: data.totals.cofinsAmount || 0,
      };
    }

    const itemsGrossTotal = items.reduce((sum, item) => sum + (item.totalValue || 0), 0);
    const itemsDiscountTotal = items.reduce((sum, item) => sum + (item.discountValue || 0), 0);
    const taxTotals = fiscalService.calculateInvoiceTaxes(items);

    return {
      productsTotal: itemsGrossTotal,
      discount: itemsDiscountTotal,
      freight: 0,
      insurance: 0,
      otherExpenses: 0,
      invoiceTotal: itemsGrossTotal - itemsDiscountTotal,
      ...taxTotals,
    };
  }, [items, data?.totals]);

  // 8. UI
  const grandTotalStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: token.colorSuccessBg,
    borderRadius: 6,
    border: `1px solid ${token.colorSuccessBorder}`,
  };

  return (
    <div>
      <Descriptions column={{ xs: 1, sm: 2, md: 2 }} size="small">
        <Descriptions.Item label={translateX('products_total')}>
          {financialService.formatCurrency(totals.productsTotal)}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('discount')}>
          {financialService.formatCurrency(totals.discount)}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('freight')}>
          {financialService.formatCurrency(totals.freight)}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('insurance')}>
          {financialService.formatCurrency(totals.insurance)}
        </Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: '12px 0' }} />

      <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
        <Descriptions.Item label="ICMS">
          {financialService.formatCurrency(totals.icmsValue)}
        </Descriptions.Item>
        <Descriptions.Item label="PIS">
          {financialService.formatCurrency(totals.pisValue)}
        </Descriptions.Item>
        <Descriptions.Item label="COFINS">
          {financialService.formatCurrency(totals.cofinsValue)}
        </Descriptions.Item>
      </Descriptions>

      <Divider style={{ margin: '12px 0' }} />

      <div style={grandTotalStyle}>
        <Title level={4} style={{ margin: 0 }}>
          {translateX('invoice_total')}
        </Title>
        <Title level={3} style={{ margin: 0, color: token.colorSuccess }}>
          {financialService.formatCurrency(totals.invoiceTotal)}
        </Title>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TotalsCard;
